import React from 'react';
import { FlaskConical, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useWriterFreeMarkSimulation } from '@/hooks/useWriterFreeMarkSimulation';
import type { WriterVersionSummary } from '@/components/writer/WriterVersionScrubber';

interface Props {
  /** Version the shown PDF was built from — recompiled after the switch flips. */
  version: WriterVersionSummary | null;
  onRecompile?: (v: WriterVersionSummary) => void | Promise<void>;
  busy?: boolean;
}

/**
 * Admin-only preview switch that forces the free-plan credit line on the next
 * compile, so both outputs (marked / clean) can be inspected from an admin
 * account. Rendered independently of `WriterFreeMarkChip`, which hides itself
 * when the version is unmarked and would otherwise hide the way back on.
 */
const WriterFreeMarkAdminToggle: React.FC<Props> = ({ version, onRecompile, busy = false }) => {
  const { isAdmin, enabled, loading, saving, setEnabled } = useWriterFreeMarkSimulation();
  if (loading || !isAdmin) return null;

  const toggle = async () => {
    const next = !enabled;
    try {
      await setEnabled(next);
      if (version && onRecompile) {
        await onRecompile(version);
        toast.success(
          next
            ? 'Simulating free plan — recompiling with the credit line'
            : 'Back to paid plan — recompiling without the credit line',
        );
      } else {
        toast.success(next ? 'Simulating free plan' : 'Back to paid plan');
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not change the preview mode');
    }
  };

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={saving || busy}
      aria-pressed={enabled}
      className={`shrink-0 inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-widest transition-colors disabled:opacity-50 ${
        enabled
          ? 'border-primary/50 text-primary'
          : 'border-border/60 text-muted-foreground hover:text-foreground'
      }`}
      title="Admin preview: compile this draft the way a free user would see it. Recompiles cost no credits."
    >
      {saving || busy ? (
        <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
      ) : (
        <FlaskConical className="h-3 w-3" aria-hidden />
      )}
      {enabled ? 'Compiling as free plan — credit line on' : 'Compiling as paid plan — clean PDF'}
    </button>
  );
};

export default WriterFreeMarkAdminToggle;
